import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HighlightedText from "components/Shared/HighlightedText";
import { BaseTabs } from "components/Shared/BaseTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useLazyListDriverForContractorQuery } from "store/services/driverApi";
import { useTablePagination } from "hooks";
import { useAuth } from "hooks/useAuth";
import { Add } from "@mui/icons-material";
import { Button } from "@mui/material";
import { format } from 'date-fns'; 
const ContractorVehicleListScreen = () => {
  const { loggedInUser } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const [sortOrder, setSortOrder] = useState("createdAt:-1");

  const breadcrumbs = [{ title: "Drivers" }];
  const driverListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [fetchDrivers, { data: driverRes, isLoading, isFetching, error }] =
    useLazyListDriverForContractorQuery();

  useEffect(() => {
    if (
      loggedInUser &&
      loggedInUser.typeRef &&
      loggedInUser.typeRefSource === "Contractor"
    ) {
      const [sortedBy, sortDirection] = sortOrder.split(":");
      fetchDrivers({
        Active: activeTab === "Active",
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
        sortedBy,
        sortDirection: parseInt(sortDirection),
        contractorId: loggedInUser.typeRef,
      });
    }
  }, [
    activeTab,
    currentPage,
    fetchDrivers,
    keyword,
    perPage,
    sortOrder,
    loggedInUser,
  ]);

 // Update these columns in your component
 const columns = [
  
  {
    name: "Vehicle ID",
    selector: (row) => row?.assigned_vehicle_ids?.map(v => v.vehicle_id).join(", "),
    cell: (row) => (
      <>
        {row?.assigned_vehicle_ids?.length > 0 ? (
          row.assigned_vehicle_ids.map((vehicle) => (
            <div key={vehicle._id}>
              <Link
                to={`/app/contractor/vehicle/${vehicle._id}/detail`}
                className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
              >
                {vehicle.vehicle_id || "N/A"}
              </Link>
            </div>
          ))
        ) : (
          "N/A"
        )}
      </>
    ),
  },
  {
    name: "Registration Number",
    selector: (row) => row?.assigned_vehicle_ids?.map(v => v.registration_number).join(", "),
    cell: (row) => (
      <>
        {row?.assigned_vehicle_ids?.length > 0 ? (
          row.assigned_vehicle_ids.map((vehicle) => (
            <div key={vehicle._id}>{vehicle.registration_number || "N/A"}</div>
          ))
        ) : (
          "N/A"
        )}
      </>
    ),
  },
  {
    name: "Ownership Type",
    selector: (row) => row?.assigned_vehicle_ids?.map(v => v.ownership_type).join(", "),
    cell: (row) => (
      <>
        {row?.assigned_vehicle_ids?.length > 0 ? (
          row.assigned_vehicle_ids.map((vehicle) => (
            <div key={vehicle._id}>{vehicle.ownership_type || "N/A"}</div>
          ))
        ) : (
          "N/A"
        )}
      </>
    ),
  },
  {
    name: "Vehicle Type",
    selector: (row) => row?.assigned_vehicle_ids?.map(v => v.vehicle_type).join(", "),
    cell: (row) => (
      <>
        {row?.assigned_vehicle_ids?.length > 0 ? (
          row.assigned_vehicle_ids.map((vehicle) => (
            <div key={vehicle._id}>{vehicle.vehicle_type || "N/A"}</div>
          ))
        ) : (
          "N/A"
        )}
      </>
    ),
  },
  {
    name: "Maintenance Due Date",
    selector: (row) => row?.assigned_vehicle_ids?.map(v => v.maintenance_due_date).join(", "),
    cell: (row) => (
      <>
        {row?.assigned_vehicle_ids?.length > 0 ? (
          row.assigned_vehicle_ids.map((vehicle) => (
            <div key={vehicle._id}>
              {vehicle.maintenance_due_date
                ? format(new Date(vehicle.maintenance_due_date), "MM/dd/yyyy") // Use your desired date format here
                : "N/A"}
            </div>
          ))
        ) : (
          "N/A"
        )}
      </>
    ),
  },
  {
    name: "Driver ID",
    selector: (row) => row?.driverId,
    cell: (row) => (
      <Link
        title={row?.driverId}
        to={`/app/contractor/driver/${row?._id}/detail`}
        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
      >
        {row?.driverId ? (
          <HighlightedText text={row.driverId} highlight={keyword} />
        ) : (
          "N/A"
        )}
      </Link>
    ),
  },
];
  

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={driverListTabs} defaultValue={activeTab} />

        <TableFilter
          searchLabel="Search Drivers"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={driverRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={driverRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => (
  <Link to="/app/contractor/vehicle/create">
    <Button startIcon={<Add />} variant="contained">
      New Vehicle
    </Button>
  </Link>
);

export default ContractorVehicleListScreen;

